import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePartnerSettings, TradeSpecialty } from '@/hooks/usePartnerSettings';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

export function TradeSpecialtiesManager() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [specialties, setSpecialties] = useState<TradeSpecialty[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState<TradeSpecialty | null>(null);
  const [formData, setFormData] = useState({ id: '', name: '' });

  useEffect(() => {
    if (settings?.trade_specialties) {
      setSpecialties(settings.trade_specialties);
    }
  }, [settings]);

  const handleSave = async () => {
    const newSpecialty: TradeSpecialty = {
      id: editingSpecialty?.id || formData.id.toLowerCase().replace(/\s+/g, '_'),
      name: formData.name,
      is_active: editingSpecialty?.is_active ?? true,
    };

    const updatedSpecialties = editingSpecialty
      ? specialties.map(s => s.id === editingSpecialty.id ? newSpecialty : s)
      : [...specialties, newSpecialty];

    await updateSettings({ trade_specialties: updatedSpecialties });
    setSpecialties(updatedSpecialties);
    setDialogOpen(false);
    setEditingSpecialty(null);
    setFormData({ id: '', name: '' });
  };

  const handleToggleActive = async (specialty: TradeSpecialty) => {
    const updatedSpecialties = specialties.map(s =>
      s.id === specialty.id ? { ...s, is_active: !s.is_active } : s
    );
    await updateSettings({ trade_specialties: updatedSpecialties });
    setSpecialties(updatedSpecialties);
  };

  const handleDelete = async (specialtyId: string) => {
    if (!confirm('Are you sure you want to delete this trade specialty?')) return;
    const updatedSpecialties = specialties.filter(s => s.id !== specialtyId);
    await updateSettings({ trade_specialties: updatedSpecialties });
    setSpecialties(updatedSpecialties);
  };

  const openEditDialog = (specialty: TradeSpecialty) => {
    setEditingSpecialty(specialty);
    setFormData({ id: specialty.id, name: specialty.name });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingSpecialty(null);
    setFormData({ id: '', name: '' });
    setDialogOpen(true);
  };

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Trade Specialties</CardTitle>
            <CardDescription>
              Manage available trade specialties for contractors
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Trade
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingSpecialty ? 'Edit Trade Specialty' : 'Add New Trade Specialty'}
                </DialogTitle>
                <DialogDescription>
                  {editingSpecialty ? 'Update the trade specialty details' : 'Create a new trade specialty option'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {!editingSpecialty && (
                  <div className="space-y-2">
                    <Label htmlFor="id">ID</Label>
                    <Input
                      id="id"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      placeholder="electrical, plumbing"
                    />
                    <p className="text-xs text-muted-foreground">
                      Unique identifier (lowercase, underscores only)
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Electrical, Plumbing"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={!formData.name || (!editingSpecialty && !formData.id)}
                >
                  {editingSpecialty ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {specialties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No trade specialties configured. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              specialties.map((specialty) => (
                <TableRow key={specialty.id}>
                  <TableCell className="font-mono text-sm">{specialty.id}</TableCell>
                  <TableCell>{specialty.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={specialty.is_active}
                        onCheckedChange={() => handleToggleActive(specialty)}
                      />
                      <Badge variant={specialty.is_active ? 'default' : 'secondary'}>
                        {specialty.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(specialty)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(specialty.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
